/**
 * Le Dilemme du jour — page autonome sur la route /jour.
 *
 * Conçu pour le format métro (90 secondes) : pas de WASM, fetch
 * léger, vote, agrégat national, partage Wordle, CTA discret vers
 * le mandat complet.
 *
 * cf. project_dilemme_du_jour.md pour le contexte stratégique.
 */

import { useEffect, useState } from "react";
import {
  fetchDilemma,
  postVote,
  todayIsoDate,
  type DilemmaAggregate,
} from "../engine/dailyApi";
import {
  appendLocalVote,
  buildShareString,
  getOrCreateDailyUuid,
  loadLocalVotes,
  type LocalVote,
} from "../engine/dailyStorage";

interface DecisionLite {
  id: string;
  kicker?: string;
  prompt: string;
  context?: string;
  options: { id: string; label: string }[];
}

const MIN_VOTES_FOR_AGGREGATE = 50;

export function DilemmaPage() {
  const [dilemma, setDilemma] = useState<DilemmaAggregate | null>(null);
  const [decision, setDecision] = useState<DecisionLite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [localVotes, setLocalVotes] = useState<LocalVote[]>(loadLocalVotes());

  const date = todayIsoDate();
  const myVote = localVotes.find((v) => v.date === date);
  const hasVoted = !!myVote;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchDilemma(date);
        if (cancelled) return;
        setDilemma(d);
        const found = await fetchDecisionLite(d.decision_id);
        if (cancelled) return;
        setDecision(found);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date]);

  async function handleVote(optionId: string) {
    if (!dilemma || voting || hasVoted) return;
    setVoting(true);
    setError(null);
    try {
      const uuid = getOrCreateDailyUuid();
      const updated = await postVote(date, optionId, uuid);
      setDilemma(updated);
      const nextVotes = appendLocalVote({
        date,
        decisionId: dilemma.decision_id,
        optionId,
      });
      setLocalVotes(nextVotes);
    } catch (e) {
      if (e instanceof Error && e.message === "ALREADY_VOTED") {
        // Vote déjà enregistré côté serveur → on remet à jour localStorage.
        setLocalVotes(
          appendLocalVote({
            date,
            decisionId: dilemma.decision_id,
            optionId,
          }),
        );
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setVoting(false);
    }
  }

  async function handleShare() {
    const url = window.location.origin;
    const text = buildShareString(date, localVotes, url);
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        setShareCopied(true);
        window.setTimeout(() => setShareCopied(false), 2500);
      }
    } catch {
      /* user cancelled */
    }
  }

  if (error) {
    return (
      <main className="dilemma-page">
        <div className="dilemma-card">
          <div className="dilemma-error">Erreur : {error}</div>
        </div>
      </main>
    );
  }

  if (!dilemma || !decision) {
    return (
      <main className="dilemma-page">
        <div className="dilemma-loading">Chargement du dilemme…</div>
      </main>
    );
  }

  const formattedDate = formatDateFr(date);
  const countByOption = new Map(
    dilemma.counts.map((c) => [c.option_id, c.count]),
  );
  const showAggregate = dilemma.total_votes >= MIN_VOTES_FOR_AGGREGATE;

  return (
    <main className="dilemma-page">
      <header className="dilemma-header">
        <div className="dilemma-brand">MOI PRÉSIDENT</div>
        <div className="dilemma-day">DILEMME DU JOUR · {formattedDate}</div>
      </header>

      <article className="dilemma-card">
        {decision.kicker && (
          <div className="dilemma-kicker">{decision.kicker}</div>
        )}
        <h1 className="dilemma-prompt">{decision.prompt}</h1>
        {decision.context && (
          <p className="dilemma-context">{decision.context}</p>
        )}

        <div className="dilemma-options">
          {decision.options.map((opt) => {
            const count = countByOption.get(opt.id) ?? 0;
            const pct =
              dilemma.total_votes > 0
                ? Math.round((count * 100) / dilemma.total_votes)
                : 0;
            const isMine = myVote?.optionId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                className="dilemma-option"
                data-voted={hasVoted ? "true" : undefined}
                data-mine={isMine ? "true" : undefined}
                onClick={() => handleVote(opt.id)}
                disabled={hasVoted || voting}
              >
                <span className="dilemma-option-label">{opt.label}</span>
                {hasVoted && showAggregate && (
                  <span className="dilemma-option-pct">{pct} %</span>
                )}
                {hasVoted && showAggregate && (
                  <span
                    className="dilemma-option-bar"
                    style={{ width: `${pct}%` }}
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>

        {hasVoted && !showAggregate && (
          <div className="dilemma-too-early">
            Soyez parmi les premiers — résultats affichés au-delà de
            {` ${MIN_VOTES_FOR_AGGREGATE} votes`}. {dilemma.total_votes} déjà
            enregistrés.
          </div>
        )}

        {hasVoted && (
          <div className="dilemma-share-row">
            <div className="dilemma-streak">
              Votre semaine : {streakGrid(date, localVotes)}
            </div>
            <button
              type="button"
              className="dilemma-share-btn"
              onClick={handleShare}
            >
              {shareCopied ? "Copié ✓" : "Partager"}
            </button>
          </div>
        )}
      </article>

      <a href="/" className="dilemma-cta">
        Curieux des conséquences réelles ?
        <span className="dilemma-cta-arrow">
          {" "}
          Jouez votre mandat complet (5 ans) →
        </span>
      </a>
    </main>
  );
}

function streakGrid(today: string, votes: LocalVote[]): string {
  const d = new Date(`${today}T00:00:00`);
  const cells: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const dd = new Date(d);
    dd.setDate(d.getDate() - i);
    const iso = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}-${String(dd.getDate()).padStart(2, "0")}`;
    cells.push(votes.some((v) => v.date === iso) ? "🟦" : "⬜");
  }
  return cells.join("");
}

function formatDateFr(iso: string): string {
  const months = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
  ];
  const [y, m, d] = iso.split("-");
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]} ${y}`;
}

async function fetchDecisionLite(id: string): Promise<DecisionLite | null> {
  // Le catalogue complet est embarqué dans le WASM côté `/`. La route
  // `/jour` ne charge pas le WASM (par doctrine — boot < 500 ms).
  // À la place, on fetche un sous-ensemble curé `dilemma_decisions.json`
  // qui ne contient QUE les décisions citées dans `daily_schedule.json`.
  // ~20 décisions, ~25 Ko — léger.
  try {
    const r = await fetch(`/data/dilemma_decisions.json`);
    if (r.ok) {
      const list = (await r.json()) as DecisionLite[];
      const found = list.find((d) => d.id === id);
      if (found) return found;
    }
  } catch {
    /* fall through */
  }
  // Fallback minimal — décision squelette à 2 options.
  return {
    id,
    kicker: "DILEMME DU JOUR",
    prompt: id.replace(/_/g, " "),
    options: [
      { id: `${id}_oui`, label: "Pour" },
      { id: `${id}_non`, label: "Contre" },
    ],
  };
}
